/* eslint-disable */
import { useState, useMemo, useRef, useEffect } from "react";
import { C, fmt$ } from "../shared/globals.js";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtM(v) {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`;
  return fmt$(v);
}

// ── Animated number ───────────────────────────────────────────────────────────
function AnimatedValue({ value, format = fmtM, duration = 600 }) {
  const [displayed, setDisplayed] = useState(value);
  const startRef   = useRef(value);
  const startTime  = useRef(null);
  const frameRef   = useRef(null);

  useEffect(() => {
    const from = startRef.current;
    const to   = value;
    if (from === to) return;
    startTime.current = null;
    cancelAnimationFrame(frameRef.current);

    const step = (ts) => {
      if (!startTime.current) startTime.current = ts;
      const p = Math.min((ts - startTime.current) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayed(from + (to - from) * eased);
      if (p < 1) frameRef.current = requestAnimationFrame(step);
      else { setDisplayed(to); startRef.current = to; }
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [value, duration]);

  return <span>{format(displayed)}</span>;
}


// All time is in years from now (year 0 = today)
function calcProjection({ startValue, monthly, annualRate, retireInYears, sellStartYear, sellPct, totalYears }) {
  const mr = annualRate / 100 / 12;
  const rows = [];
  let balance = startValue;
  let cumulativeSold = 0;

  for (let y = 0; y <= totalYears; y++) {
    const isAccumulating = y < retireInYears;
    const isSelling = sellPct > 0 && y >= sellStartYear;

    for (let m = 0; m < 12; m++) {
      if (y === 0 && m === 0) continue;
      balance = balance * (1 + mr) + (isAccumulating ? monthly : 0);
    }

    let soldThisYear = 0;
    if (isSelling && balance > 0) {
      soldThisYear = balance * (sellPct / 100);
      balance -= soldThisYear;
      cumulativeSold += soldThisYear;
    }

    rows.push({
      year: y,
      balance: Math.max(balance, 0),
      soldThisYear,
      monthlyIncome: soldThisYear / 12,
      cumulativeSold,
      isSelling,
      isAccumulating,
      totalInvested: startValue + monthly * 12 * Math.min(y, retireInYears),
    });
  }
  return rows;
}

// ── SVG Chart ─────────────────────────────────────────────────────────────────
function RetirementChart({ rows, sellStartYear, retireInYears }) {
  const W = 620, H = 240, PL = 72, PR = 16, PT = 16, PB = 36;
  const cW = W - PL - PR, cH = H - PT - PB;

  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const maxVal  = Math.max(...rows.map(r => r.balance), ...rows.map(r => r.cumulativeSold), 1);
  const maxYear = rows[rows.length - 1]?.year ?? 1;

  const xS = yr  => PL + (yr / (maxYear || 1)) * cW;
  const yS = val => PT + cH - (Math.min(val, maxVal) / maxVal) * cH;

  const toPath = getter =>
    rows.map((r, i) => `${i === 0 ? "M" : "L"}${xS(r.year).toFixed(1)},${yS(getter(r)).toFixed(1)}`).join(" ");

  const balancePath  = toPath(r => r.balance);
  const investedPath = toPath(r => r.totalInvested);
  const cumulPath    = rows.some(r => r.cumulativeSold > 0) ? toPath(r => r.cumulativeSold) : null;

  const areaPath = balancePath +
    ` L${xS(maxYear).toFixed(1)},${(PT + cH).toFixed(1)} L${xS(0).toFixed(1)},${(PT + cH).toFixed(1)} Z`;

  const yTicks  = [0, 0.25, 0.5, 0.75, 1].map(t => ({ val: maxVal * t, y: yS(maxVal * t) }));
  const xTicks  = rows.filter(r => r.year % 5 === 0 || r.year === maxYear);
  const retireX = retireInYears <= maxYear ? xS(retireInYears) : null;
  const sellX   = sellStartYear != null && sellStartYear <= maxYear ? xS(sellStartYear) : null;

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const pct  = (e.clientX - rect.left) / rect.width;
    const svgX = pct * W;
    const yearF = ((svgX - PL) / cW) * maxYear;
    const idx  = Math.max(0, Math.min(rows.length - 1, Math.round(yearF)));
    setHover({ row: rows[idx], svgX: xS(rows[idx].year), pct });
  };

  return (
    <div style={{ position:"relative" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width:"100%", height:"auto", display:"block", cursor:"crosshair" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={C.orange} stopOpacity="0.15"/>
            <stop offset="100%" stopColor={C.orange} stopOpacity="0.01"/>
          </linearGradient>
        </defs>

        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PL} y1={t.y} x2={W - PR} y2={t.y} stroke="var(--border)" strokeWidth="1" strokeDasharray="3,3"/>
            <text x={PL - 6} y={t.y + 4} textAnchor="end" fill="var(--text4)" fontSize="9" fontFamily="Montserrat,sans-serif">{fmtM(t.val)}</text>
          </g>
        ))}

        {retireX && <>
          <line x1={retireX} y1={PT} x2={retireX} y2={PT + cH} stroke={C.orange} strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>
          <text x={retireX + 4} y={PT + 12} fill={C.orange} fontSize="8" fontFamily="Montserrat,sans-serif" opacity="0.8">retire</text>
        </>}

        {sellX && <rect x={sellX} y={PT} width={W - PR - sellX} height={cH} fill="rgba(96,165,250,0.04)" rx="2"/>}
        {sellX && <>
          <line x1={sellX} y1={PT} x2={sellX} y2={PT + cH} stroke="#60a5fa" strokeWidth="1" strokeDasharray="4,3" opacity="0.6"/>
          <text x={sellX + 4} y={PT + 24} fill="#60a5fa" fontSize="8" fontFamily="Montserrat,sans-serif" opacity="0.8">sell</text>
        </>}

        <path d={areaPath} fill="url(#retGrad)"/>
        <path d={investedPath} fill="none" stroke="var(--border2)" strokeWidth="1.5" strokeDasharray="4,3"/>
        {cumulPath && <path d={cumulPath} fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeDasharray="3,2"/>}
        <path d={balancePath} fill="none" stroke={C.orange} strokeWidth="2"/>

        {xTicks.map((r, i) => (
          <text key={i} x={xS(r.year)} y={H - 8} textAnchor="middle" fill="var(--text4)" fontSize="9" fontFamily="Montserrat,sans-serif">
            {r.year === 0 ? "Now" : `Yr ${r.year}`}
          </text>
        ))}

        {/* Final dot (hidden during hover) */}
        {!hover && rows.length > 0 && (() => {
          const last = rows[rows.length - 1];
          return <circle cx={xS(last.year)} cy={yS(last.balance)} r="4" fill={C.orange} stroke="var(--bg2)" strokeWidth="2"/>;
        })()}

        {/* Hover crosshair + dots */}
        {hover && <>
          <line x1={hover.svgX} y1={PT} x2={hover.svgX} y2={PT + cH} stroke="var(--text4)" strokeWidth="1" strokeDasharray="3,3" pointerEvents="none"/>
          <circle cx={hover.svgX} cy={yS(hover.row.balance)} r="4.5" fill={C.orange} stroke="var(--bg2)" strokeWidth="2" pointerEvents="none"/>
          {hover.row.cumulativeSold > 0 && (
            <circle cx={hover.svgX} cy={yS(hover.row.cumulativeSold)} r="4" fill="#60a5fa" stroke="var(--bg2)" strokeWidth="2" pointerEvents="none"/>
          )}
          <circle cx={hover.svgX} cy={yS(hover.row.totalInvested)} r="3.5" fill="var(--text4)" stroke="var(--bg2)" strokeWidth="2" pointerEvents="none"/>
        </>}

        {/* Invisible mouse capture overlay */}
        <rect x={PL} y={PT} width={cW} height={cH} fill="transparent"/>
      </svg>

      {/* Tooltip */}
      {hover && (() => {
        const r = hover.row;
        const flipLeft = hover.pct > 0.62;
        const phaseLabel = r.isAccumulating ? "Growth" : r.isSelling ? "Withdrawal" : "Coasting";
        const phaseColor = r.isAccumulating ? C.orange : r.isSelling ? "#60a5fa" : "var(--text4)";
        return (
          <div style={{
            position:"absolute", top:"8%",
            ...(flipLeft ? { right:"4%" } : { left:"12%" }),
            background:"var(--bg3)", border:"1px solid var(--border2)",
            borderRadius:10, padding:"12px 14px", pointerEvents:"none",
            minWidth:190, zIndex:10, boxShadow:"0 4px 24px rgba(0,0,0,0.45)",
          }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:900, color:"var(--text)" }}>
                {r.year === 0 ? "Now" : `Year ${r.year}`}
              </span>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:phaseColor, background:`${phaseColor}18`, padding:"2px 7px", borderRadius:4, letterSpacing:"0.06em", textTransform:"uppercase" }}>
                {phaseLabel}
              </span>
            </div>
            {/* Rows */}
            {[
              { label:"Balance",     value:fmtM(r.balance),                  color:C.orange },
              { label:"Contributed", value:fmtM(r.totalInvested),            color:"var(--text)" },
              { label:"Growth",      value:fmtM(r.balance - r.totalInvested), color:"#4ade80" },
              ...(r.soldThisYear > 0 ? [
                null,
                { label:"Withdrawn / yr",  value:fmtM(r.soldThisYear),    color:"#60a5fa" },
                { label:"Monthly income",  value:fmtM(r.monthlyIncome),   color:"#4ade80" },
                { label:"Total withdrawn", value:fmtM(r.cumulativeSold),  color:"var(--text3)" },
              ] : []),
            ].map((item, i) =>
              item === null
                ? <div key={i} style={{ height:1, background:"var(--border)", margin:"6px 0" }}/>
                : (
                  <div key={i} style={{ display:"flex", justifyContent:"space-between", gap:16, marginBottom:4 }}>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>{item.label}</span>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:item.color }}>{item.value}</span>
                  </div>
                )
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
function InputRow({ label, value, onChange, prefix = "$", suffix = "", min = 0, max = 99, step = 1 }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color:"var(--text3)", letterSpacing:"0.08em", textTransform:"uppercase" }}>{label}</span>
      <div style={{ position:"relative", display:"flex", alignItems:"center" }}>
        {prefix && <span style={{ position:"absolute", left:10, fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color:"var(--text3)", pointerEvents:"none" }}>{prefix}</span>}
        <input
          type="number" value={value} min={min} max={max} step={step}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            width:"100%", background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8,
            padding:`10px ${suffix ? 32 : 12}px 10px ${prefix ? 24 : 12}px`,
            fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:700, color:"var(--text)",
            outline:"none", boxSizing:"border-box", WebkitAppearance:"none", MozAppearance:"textfield",
          }}
          onFocus={e => e.currentTarget.style.borderColor = C.orange}
          onBlur={e  => e.currentTarget.style.borderColor = "var(--border)"}
        />
        {suffix && <span style={{ position:"absolute", right:10, fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color:"var(--text3)", pointerEvents:"none" }}>{suffix}</span>}
      </div>
    </div>
  );
}

function StatCard({ label, rawValue, value, color, sub }) {
  return (
    <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, padding:"14px 16px", flex:1, minWidth:130 }}>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:6, textTransform:"uppercase" }}>{label}</div>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:18, fontWeight:900, color: color || "var(--text)", lineHeight:1 }}>
        {rawValue != null ? <AnimatedValue value={rawValue}/> : value}
      </div>
      {sub && <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)", marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color:"var(--text3)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12 }}>{children}</div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function RetirementPage({ portfolioTotalValue = 0, isMobile = false }) {
  const [usePortfolioValue, setUsePortfolioValue] = useState(portfolioTotalValue > 0);
  const [manualStart,    setManualStart]    = useState(10000);
  const [monthly,        setMonthly]        = useState(500);
  const [annualRate,     setAnnualRate]     = useState(9);
  const [retireInYears,  setRetireInYears]  = useState(25);
  const [sellEnabled,    setSellEnabled]    = useState(true);
  const [sellStartYear,  setSellStartYear]  = useState(5);
  const [sellPct,        setSellPct]        = useState(10);
  const [sellDuration,   setSellDuration]   = useState(20);
  const [targetMonthly,  setTargetMonthly]  = useState(3000);
  const [exporting,      setExporting]      = useState(false);

  const pageRef = useRef(null);

  const exportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Load libs via script tags if not already loaded
      const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src; s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });

      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      const el     = pageRef.current;
      const canvas = await window.html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#0a0a0a",
        logging: false,
      });

      const { jsPDF } = window.jspdf;
      const pdf       = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
      const pdfW      = pdf.internal.pageSize.getWidth();
      const pdfH      = pdf.internal.pageSize.getHeight();
      const ratio     = pdfW / canvas.width;
      const totalH    = canvas.height * ratio;
      const imgData   = canvas.toDataURL("image/png");

      let yOffset = 0;
      while (yOffset < totalH) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, -yOffset, pdfW, totalH);
        yOffset += pdfH;
      }

      pdf.save(`retirement-plan-${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (e) {
      console.error("PDF export failed:", e);
      alert("Export failed. Check console for details.");
    }
    setExporting(false);
  };

  const startValue  = usePortfolioValue ? portfolioTotalValue : manualStart;
  const sellEndYear = sellStartYear + sellDuration;
  const totalYears  = sellEnabled ? Math.max(sellEndYear, retireInYears) : retireInYears;

  const rows = useMemo(() => calcProjection({
    startValue, monthly, annualRate, retireInYears,
    sellStartYear: sellEnabled ? sellStartYear : 9999,
    sellPct:       sellEnabled ? sellPct : 0,
    totalYears,
  }), [startValue, monthly, annualRate, retireInYears, sellEnabled, sellStartYear, sellPct, sellDuration, totalYears]);

  const atRetire    = rows.find(r => r.year === retireInYears) || rows[rows.length - 1];
  const finalRow    = rows[rows.length - 1];
  const totalSold   = finalRow?.cumulativeSold ?? 0;
  const peakBalance = Math.max(...rows.map(r => r.balance));

  const PRESETS = [
    { label:"Conservative", rate:6,  icon:"ph-leaf" },
    { label:"Moderate",     rate:9,  icon:"ph-chart-line-up" },
    { label:"Aggressive",   rate:14, icon:"ph-rocket-launch" },
  ];

  return (
    <div ref={pageRef} style={{ padding: isMobile ? "16px 0" : "0 0 60px 0" }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ marginBottom:22 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:6 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <i className="ph ph-shooting-star" style={{ fontSize:22, color:C.orange }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:20, fontWeight:900, color:"var(--text)", letterSpacing:"-0.02em" }}>Retirement Calculator</span>
          </div>
          {/* PDF Export button */}
          <button
            onClick={exportPDF}
            disabled={exporting}
            style={{
              background: exporting ? "var(--bg3)" : "transparent",
              border:`1px solid ${exporting ? "var(--border)" : "var(--border2)"}`,
              borderRadius:8, padding:"7px 14px", cursor: exporting ? "not-allowed" : "pointer",
              display:"flex", alignItems:"center", gap:7, transition:"all 0.15s", flexShrink:0,
            }}
            onMouseEnter={e => { if (!exporting) { e.currentTarget.style.borderColor = C.orange; e.currentTarget.style.background = "rgba(242,135,5,0.06)"; }}}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border2)"; e.currentTarget.style.background = "transparent"; }}
          >
            <i className={`ph ${exporting ? "ph-circle-notch" : "ph-file-pdf"}`}
              style={{ fontSize:16, color: exporting ? "var(--text4)" : C.orange,
                ...(exporting ? { animation:"spin 1s linear infinite" } : {}) }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
              color: exporting ? "var(--text4)" : "var(--text3)", letterSpacing:"0.04em" }}>
              {exporting ? "Exporting…" : "Export PDF"}
            </span>
          </button>
        </div>
        <p style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, color:"var(--text3)", margin:0 }}>
          Project growth, model withdrawals, and plan your income year by year.
        </p>
      </div>

      {/* Presets */}
      <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
        {PRESETS.map(p => (
          <button key={p.label} onClick={() => setAnnualRate(p.rate)} style={{
            background: annualRate === p.rate ? "rgba(242,135,5,0.12)" : "var(--bg3)",
            border:`1px solid ${annualRate === p.rate ? C.orange : "var(--border)"}`,
            borderRadius:8, padding:"8px 14px", cursor:"pointer",
            display:"flex", alignItems:"center", gap:6, transition:"all 0.15s",
          }}>
            <i className={`ph ${p.icon}`} style={{ fontSize:14, color: annualRate === p.rate ? C.orange : "var(--text3)" }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color: annualRate === p.rate ? C.orange : "var(--text3)", letterSpacing:"0.04em" }}>{p.label}</span>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>{p.rate}%</span>
          </button>
        ))}
      </div>

      {/* Growth Inputs — Split layout */}
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, marginBottom:14, overflow:"hidden" }}>

        {/* Top label */}
        <div style={{ padding:"14px 20px 0 20px" }}>
          <SectionLabel>Growth Settings</SectionLabel>
        </div>

        {/* Split body */}
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1px 1fr", gap:0 }}>

          {/* LEFT — asset-style value card, aligned to top */}
          <div style={{ padding:"0 20px 24px 20px", display:"flex", flexDirection:"column", alignItems:"flex-start" }}>

            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10 }}>Starting Value</div>

            {/* Fixed-size card — never jumps */}
            <div style={{
              background:"var(--bg3)",
              border:`1.5px solid ${usePortfolioValue && portfolioTotalValue > 0 ? "#4ade80" : C.orange}`,
              borderRadius:10,
              padding:"0 18px",
              width:"100%", maxWidth:240,
              height:62,
              boxSizing:"border-box",
              boxShadow: usePortfolioValue && portfolioTotalValue > 0
                ? "0 0 14px rgba(74,222,128,0.18)"
                : "0 0 14px rgba(242,135,5,0.14)",
              transition:"border-color 0.25s, box-shadow 0.25s",
              display:"flex", alignItems:"center",
            }}>
              {/* Live mode — animated value */}
              <div style={{
                display: usePortfolioValue ? "flex" : "none",
                alignItems:"center", width:"100%",
              }}>
                {portfolioTotalValue > 0 ? (
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:28, fontWeight:900, color:"#4ade80", letterSpacing:"-0.02em", lineHeight:1 }}>
                    <AnimatedValue value={portfolioTotalValue}/>
                  </div>
                ) : (
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text4)", lineHeight:1.5 }}>
                    ⚠️ No value yet.<br/>Add assets or use Manual.
                  </div>
                )}
              </div>
              {/* Manual mode — input, always same height */}
              <div style={{
                display: !usePortfolioValue ? "flex" : "none",
                alignItems:"center", gap:4, width:"100%",
              }}>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:18, fontWeight:700, color:C.orange, flexShrink:0, lineHeight:1 }}>$</span>
                <input
                  type="number"
                  value={manualStart}
                  min={0} max={100000000} step={1000}
                  onChange={e => setManualStart(Number(e.target.value))}
                  style={{
                    background:"transparent", border:"none", outline:"none",
                    fontFamily:"Montserrat,sans-serif", fontSize:28, fontWeight:900,
                    color:C.orange, width:"100%", minWidth:0, lineHeight:1,
                    WebkitAppearance:"none", MozAppearance:"textfield",
                  }}
                />
              </div>
            </div>

            {/* Status dot */}
            <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:8 }}>
              <div style={{
                width:6, height:6, borderRadius:"50%", flexShrink:0,
                background: usePortfolioValue && portfolioTotalValue > 0 ? "#4ade80" : C.orange,
                boxShadow: usePortfolioValue && portfolioTotalValue > 0 ? "0 0 6px #4ade80" : `0 0 6px ${C.orange}`,
              }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color:"var(--text4)", letterSpacing:"0.06em", textTransform:"uppercase" }}>
                {usePortfolioValue && portfolioTotalValue > 0 ? "Live" : "Manual"}
              </span>
            </div>

            {/* Toggles BELOW the card */}
            <div style={{ display:"flex", gap:6, marginTop:14 }}>
              <button onClick={() => setUsePortfolioValue(true)} style={{
                background: usePortfolioValue ? "rgba(74,222,128,0.1)" : "transparent",
                border:`1px solid ${usePortfolioValue ? "#4ade80" : "var(--border)"}`,
                borderRadius:20, padding:"4px 12px", cursor:"pointer", transition:"all 0.15s",
                display:"flex", alignItems:"center", gap:5,
              }}>
                <i className="ph ph-link" style={{ fontSize:11, color: usePortfolioValue ? "#4ade80" : "var(--text4)" }}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color: usePortfolioValue ? "#4ade80" : "var(--text4)", letterSpacing:"0.04em" }}>Portfolio</span>
              </button>
              <button onClick={() => setUsePortfolioValue(false)} style={{
                background: !usePortfolioValue ? "rgba(242,135,5,0.12)" : "transparent",
                border:`1px solid ${!usePortfolioValue ? C.orange : "var(--border)"}`,
                borderRadius:20, padding:"4px 12px", cursor:"pointer", transition:"all 0.15s",
                display:"flex", alignItems:"center", gap:5,
              }}>
                <i className="ph ph-pencil-simple" style={{ fontSize:11, color: !usePortfolioValue ? C.orange : "var(--text4)" }}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color: !usePortfolioValue ? C.orange : "var(--text4)", letterSpacing:"0.04em" }}>Manual</span>
              </button>
            </div>

          </div>

          {/* Divider */}
          {!isMobile && <div style={{ background:"var(--border)", width:1 }}/>}

          {/* RIGHT — three inputs, aligned to top */}
          <div style={{ padding:"0 20px 24px 20px", display:"flex", flexDirection:"column", gap:12 }}>
            <InputRow label="Monthly Contribution" value={monthly}       onChange={setMonthly}       min={0} max={100000} step={50} />
            <InputRow label="Annual Return"         value={annualRate}    onChange={setAnnualRate}    min={0} max={50}     step={0.5} suffix="%" prefix=""/>
            <InputRow label="Years to Goal"         value={retireInYears} onChange={v => setRetireInYears(Math.max(v, 1))} min={1} max={80} step={1} prefix="" suffix="yrs"/>
          </div>

        </div>
      </div>

      {/* Selling Phase */}
      <div style={{ background:"var(--bg2)", border:`1px solid ${sellEnabled ? "#60a5fa55" : "var(--border)"}`, borderRadius:12, padding:"18px 20px", marginBottom:20, transition:"border-color 0.2s" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: sellEnabled ? 16 : 0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <i className="ph ph-money-wavy" style={{ fontSize:16, color: sellEnabled ? "#60a5fa" : "var(--text3)" }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color: sellEnabled ? "#60a5fa" : "var(--text3)", letterSpacing:"0.1em", textTransform:"uppercase" }}>Withdrawal / Selling Phase</span>
          </div>
          <button onClick={() => setSellEnabled(v => !v)} style={{
            background: sellEnabled ? "#60a5fa22" : "var(--bg3)",
            border:`1px solid ${sellEnabled ? "#60a5fa" : "var(--border)"}`,
            borderRadius:20, padding:"5px 16px", cursor:"pointer", transition:"all 0.15s",
            fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
            color: sellEnabled ? "#60a5fa" : "var(--text3)",
          }}>
            {sellEnabled ? "ON" : "OFF"}
          </button>
        </div>

        {sellEnabled && (
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap:12 }}>
            <InputRow label="Start Selling After" value={sellStartYear} onChange={v => setSellStartYear(Math.max(v, 1))}     min={1}  max={200} step={1} prefix="" suffix="yrs"/>
            <InputRow label="Sell % per Year"      value={sellPct}       onChange={setSellPct}                                min={1}  max={100} step={1} suffix="%" prefix=""/>
            <InputRow label="Sell for Duration"    value={sellDuration}  onChange={v => setSellDuration(Math.max(v, 1))}     min={1}  max={100} step={1} prefix="" suffix="yrs"/>
          </div>
        )}
        {sellEnabled && (
          <div style={{ marginTop:10, fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text4)" }}>
            Selling {sellPct}%/yr from <span style={{ color:"#60a5fa", fontWeight:700 }}>year {sellStartYear}</span> to <span style={{ color:"#60a5fa", fontWeight:700 }}>year {sellEndYear}</span>
          </div>
        )}
      </div>

      {/* Stat Cards */}
      <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap" }}>
        <StatCard
          label="Balance at Goal"
          rawValue={atRetire?.balance ?? 0}
          color={C.orange}
          sub={`After ${retireInYears} years`}
        />
        <StatCard
          label="Total Contributed"
          rawValue={atRetire?.totalInvested ?? 0}
          color="var(--text)"
          sub={`${fmt$(startValue)} + ${fmt$(monthly)}/mo`}
        />
        <StatCard
          label="Peak Portfolio"
          rawValue={peakBalance}
          color="#4ade80"
          sub="Highest balance"
        />
        {sellEnabled && (
          <StatCard
            label="Total Withdrawn"
            rawValue={totalSold}
            color="#60a5fa"
            sub={`${sellPct}%/yr · ${sellDuration} years`}
          />
        )}
        {sellEnabled && (
          <StatCard
            label="Remaining Balance"
            rawValue={finalRow?.balance ?? 0}
            color={finalRow?.balance > 0 ? "var(--text)" : "#f87171"}
            sub={`After year ${totalYears}`}
          />
        )}
      </div>

      {/* Chart */}
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"20px", marginBottom:20 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text3)", letterSpacing:"0.08em", textTransform:"uppercase" }}>Projected Growth</span>
          <div style={{ display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
            {[
              { color:C.orange,         label:"Balance" },
              { color:"var(--border2)", label:"Contributed", dashed:true },
              ...(sellEnabled ? [{ color:"#60a5fa", label:"Cumul. Withdrawn", dashed:true }] : []),
            ].map((l, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ width:18, height:2, borderRadius:2, ...(l.dashed
                  ? { backgroundImage:`repeating-linear-gradient(90deg,${l.color} 0,${l.color} 4px,transparent 4px,transparent 7px)` }
                  : { background:l.color }
                )}}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
        {rows.length > 1
          ? <RetirementChart rows={rows} sellStartYear={sellEnabled ? sellStartYear : null} retireInYears={retireInYears}/>
          : <div style={{ textAlign:"center", padding:"40px 0", fontFamily:"Montserrat,sans-serif", fontSize:12, color:"var(--text4)" }}>Set years to goal above 0.</div>
        }
      </div>

      {/* ── Breakdown Donut + Monthly Income Target ── */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:14, marginBottom:20 }}>

        {/* Breakdown Donut */}
        {(() => {
          const bal      = atRetire?.balance ?? 0;
          const invested = atRetire?.totalInvested ?? 0;
          const growth   = Math.max(bal - invested, 0);
          const total    = invested + growth || 1;
          const invPct   = invested / total;
          const grwPct   = growth / total;

          // SVG donut
          const R = 54, r = 36, cx = 70, cy = 70;
          const circ = 2 * Math.PI * R;
          const invDash = invPct * circ;
          const grwDash = grwPct * circ;
          // start invested from top (-90deg = -PI/2)
          const invOffset = 0;
          const grwOffset = -(invDash);

          return (
            <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"20px" }}>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text3)", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:16 }}>
                At Goal — Breakdown
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:20 }}>
                {/* SVG */}
                <svg width="140" height="140" viewBox="0 0 140 140" style={{ flexShrink:0 }}>
                  <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--bg3)" strokeWidth={R - r}/>
                  {/* Invested arc */}
                  <circle cx={cx} cy={cy} r={R} fill="none"
                    stroke={C.orange} strokeWidth={R - r}
                    strokeDasharray={`${invDash} ${circ - invDash}`}
                    strokeDashoffset={circ / 4}
                    style={{ transition:"stroke-dasharray 0.6s ease" }}
                  />
                  {/* Growth arc */}
                  <circle cx={cx} cy={cy} r={R} fill="none"
                    stroke="#4ade80" strokeWidth={R - r}
                    strokeDasharray={`${grwDash} ${circ - grwDash}`}
                    strokeDashoffset={circ / 4 - invDash}
                    style={{ transition:"stroke-dasharray 0.6s ease" }}
                  />
                  {/* Center label */}
                  <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--text)" fontSize="13" fontWeight="900" fontFamily="Montserrat,sans-serif">
                    {fmtM(bal)}
                  </text>
                  <text x={cx} y={cy + 10} textAnchor="middle" fill="var(--text4)" fontSize="8" fontFamily="Montserrat,sans-serif" letterSpacing="0.06em">
                    TOTAL
                  </text>
                </svg>
                {/* Legend */}
                <div style={{ display:"flex", flexDirection:"column", gap:12, flex:1 }}>
                  {[
                    { label:"Contributed", value:invested, color:C.orange,  pct:invPct },
                    { label:"Growth",      value:growth,   color:"#4ade80",  pct:grwPct },
                  ].map((item, i) => (
                    <div key={i}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:item.color, flexShrink:0 }}/>
                          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>{item.label}</span>
                        </div>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color:item.color }}>
                          {(item.pct * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:900, color:"var(--text)", marginLeft:14 }}>
                        <AnimatedValue value={item.value}/>
                      </div>
                      {/* Mini bar */}
                      <div style={{ marginLeft:14, marginTop:4, height:3, background:"var(--bg3)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${item.pct * 100}%`, background:item.color, borderRadius:2, transition:"width 0.6s ease" }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Monthly Income Target — reverse calculator */}
        {(() => {
          const annualNeed  = targetMonthly * 12;
          // 4% rule: need = annual / 0.04
          const fireNumber  = annualNeed / 0.04;
          const currentBal  = atRetire?.balance ?? 0;
          const gap         = Math.max(fireNumber - currentBal, 0);
          const isAchieved  = currentBal >= fireNumber;
          // Years to reach fireNumber from now (simple compound)
          const mr = annualRate / 100 / 12;
          let yearsToFire = null;
          if (!isAchieved && mr > 0) {
            // FV = PV*(1+r)^n + PMT*((1+r)^n - 1)/r  → solve for n numerically
            let bal = startValue, n = 0;
            while (bal < fireNumber && n < 200) {
              bal = bal * (1 + mr) + monthly;
              n++;
            }
            yearsToFire = n < 200 ? Math.ceil(n / 12) : null;
          }

          return (
            <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"20px" }}>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text3)", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:16 }}>
                Monthly Income Target
              </div>

              {/* Input */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6 }}>
                  I want per month
                </div>
                <div style={{ position:"relative", display:"flex", alignItems:"center" }}>
                  <span style={{ position:"absolute", left:10, fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color:"var(--text3)", pointerEvents:"none" }}>$</span>
                  <input
                    type="number" value={targetMonthly} min={100} max={1000000} step={100}
                    onChange={e => setTargetMonthly(Number(e.target.value))}
                    style={{
                      width:"100%", background:"var(--bg3)", border:`1px solid ${isAchieved ? "#4ade80" : "var(--border)"}`,
                      borderRadius:8, padding:"10px 12px 10px 24px",
                      fontFamily:"Montserrat,sans-serif", fontSize:16, fontWeight:700, color:"var(--text)",
                      outline:"none", boxSizing:"border-box", WebkitAppearance:"none", MozAppearance:"textfield",
                      transition:"border-color 0.2s",
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = C.orange}
                    onBlur={e  => e.currentTarget.style.borderColor = isAchieved ? "#4ade80" : "var(--border)"}
                  />
                </div>
              </div>

              {/* FIRE number */}
              <div style={{ background:"var(--bg3)", borderRadius:10, padding:"12px 14px", marginBottom:12 }}>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4 }}>
                  Portfolio needed (4% rule)
                </div>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:22, fontWeight:900, color:"var(--text)", letterSpacing:"-0.02em" }}>
                  <AnimatedValue value={fireNumber}/>
                </div>
              </div>

              {/* Status */}
              {isAchieved ? (
                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.25)", borderRadius:8 }}>
                  <i className="ph ph-check-circle" style={{ fontSize:18, color:"#4ade80", flexShrink:0 }}/>
                  <div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"#4ade80" }}>Goal achieved at retirement</div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)", marginTop:2 }}>
                      Surplus: <AnimatedValue value={currentBal - fireNumber}/>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>Gap at retirement</span>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"#f87171" }}>
                      <AnimatedValue value={gap}/>
                    </span>
                  </div>
                  {yearsToFire != null && (
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>Years to reach FIRE</span>
                      <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:C.orange }}>{yearsToFire} yrs</span>
                    </div>
                  )}
                  {/* Progress bar */}
                  <div style={{ marginTop:4, height:4, background:"var(--bg3)", borderRadius:2, overflow:"hidden" }}>
                    <div style={{
                      height:"100%", borderRadius:2, transition:"width 0.6s ease",
                      width:`${Math.min((currentBal / fireNumber) * 100, 100)}%`,
                      background: currentBal / fireNumber > 0.75 ? "#4ade80" : currentBal / fireNumber > 0.4 ? C.orange : "#f87171",
                    }}/>
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", textAlign:"right" }}>
                    {((currentBal / fireNumber) * 100).toFixed(1)}% of FIRE number
                  </div>
                </div>
              )}
            </div>
          );
        })()}

      </div>

      {/* ── Contribution vs Growth Bar Chart ── */}
      {(() => {
        // Build decade buckets: 0-9, 10-19, 20-29, etc.
        const buckets = [];
        const decadeSize = 10;
        const maxYr = rows[rows.length - 1]?.year ?? 0;
        for (let d = 0; d < maxYr; d += decadeSize) {
          const start = d;
          const end   = Math.min(d + decadeSize - 1, maxYr);
          const rStart = rows.find(r => r.year === start);
          const rEnd   = rows.find(r => r.year === end) || rows[rows.length - 1];
          if (!rEnd) continue;
          const contributed = rEnd.totalInvested - (rStart?.totalInvested ?? 0);
          const balanceGain = rEnd.balance - (rStart?.balance ?? startValue);
          const growth      = Math.max(balanceGain - contributed, 0);
          buckets.push({
            label: end >= maxYr ? `Yr ${start}+` : `Yr ${start}–${end}`,
            contributed: Math.max(contributed, 0),
            growth:      Math.max(growth, 0),
          });
        }

        const maxTotal = Math.max(...buckets.map(b => b.contributed + b.growth), 1);

        return (
          <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, padding:"20px", marginBottom:20 }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:8 }}>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text3)", letterSpacing:"0.08em", textTransform:"uppercase" }}>
                Contribution vs Growth — by Decade
              </span>
              <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                {[
                  { color:C.orange,  label:"Contributed" },
                  { color:"#4ade80", label:"Compound Growth" },
                ].map((l, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <div style={{ width:10, height:10, borderRadius:2, background:l.color }}/>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)" }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bars */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {buckets.map((b, i) => {
                const total     = b.contributed + b.growth;
                const contribW  = (b.contributed / maxTotal) * 100;
                const growthW   = (b.growth / maxTotal) * 100;
                const growthPct = total > 0 ? (b.growth / total * 100).toFixed(0) : 0;

                return (
                  <div key={i} style={{ display:"grid", gridTemplateColumns:"70px 1fr 80px", gap:10, alignItems:"center" }}>
                    {/* Label */}
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color:"var(--text4)", textAlign:"right", letterSpacing:"0.02em" }}>
                      {b.label}
                    </span>

                    {/* Stacked bar */}
                    <div style={{ position:"relative", height:28, background:"var(--bg3)", borderRadius:6, overflow:"hidden" }}>
                      {/* Contributed segment */}
                      <div style={{
                        position:"absolute", left:0, top:0, bottom:0,
                        width:`${contribW}%`,
                        background:C.orange,
                        borderRadius: growthW > 0 ? "6px 0 0 6px" : "6px",
                        transition:"width 0.6s ease",
                        display:"flex", alignItems:"center", paddingLeft:8, overflow:"hidden",
                      }}>
                        {contribW > 15 && (
                          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"rgba(0,0,0,0.6)", whiteSpace:"nowrap" }}>
                            {fmtM(b.contributed)}
                          </span>
                        )}
                      </div>
                      {/* Growth segment */}
                      <div style={{
                        position:"absolute", left:`${contribW}%`, top:0, bottom:0,
                        width:`${growthW}%`,
                        background:"#4ade80",
                        borderRadius: contribW > 0 ? "0 6px 6px 0" : "6px",
                        transition:"width 0.6s ease, left 0.6s ease",
                        display:"flex", alignItems:"center", paddingLeft:8, overflow:"hidden",
                      }}>
                        {growthW > 15 && (
                          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"rgba(0,0,0,0.6)", whiteSpace:"nowrap" }}>
                            {fmtM(b.growth)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: total + growth% badge */}
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:1 }}>
                      <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text)", lineHeight:1 }}>
                        {fmtM(total)}
                      </span>
                      {b.growth > 0 && (
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"#4ade80", letterSpacing:"0.04em" }}>
                          +{growthPct}% growth
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary line */}
            {buckets.length > 0 && (() => {
              const lastB = buckets[buckets.length - 1];
              const allContrib = buckets.reduce((s, b) => s + b.contributed, 0);
              const allGrowth  = buckets.reduce((s, b) => s + b.growth, 0);
              const allTotal   = allContrib + allGrowth || 1;
              return (
                <div style={{ marginTop:16, paddingTop:14, borderTop:"1px solid var(--border)", display:"flex", gap:20, flexWrap:"wrap" }}>
                  {[
                    { label:"Total contributed", value:allContrib, color:C.orange },
                    { label:"Total growth",       value:allGrowth,  color:"#4ade80" },
                    { label:"Growth multiplier",  value:null, extra:`${(allTotal / Math.max(allContrib, 1)).toFixed(1)}×`, color:"var(--text)" },
                  ].map((s, i) => (
                    <div key={i}>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:2 }}>{s.label}</div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:900, color:s.color }}>
                        {s.extra ?? <AnimatedValue value={s.value}/>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Year-by-Year Table */}
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:8 }}>
          <i className="ph ph-table" style={{ fontSize:15, color:"var(--text3)" }}/>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text3)", letterSpacing:"0.08em", textTransform:"uppercase" }}>Year-by-Year Breakdown</span>
        </div>
        <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth: sellEnabled ? 640 : 380 }}>
            <thead>
              <tr style={{ background:"var(--bg3)" }}>
                {["Year", "Portfolio", ...(sellEnabled ? ["Withdrawn / Year", "Monthly Income", "Total Withdrawn"] : []), "Phase"].map((h, i) => (
                  <th key={i} style={{
                    padding:"10px 16px", textAlign: i === 0 ? "center" : "right",
                    fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                    color:"var(--text4)", letterSpacing:"0.08em", textTransform:"uppercase",
                    borderBottom:"1px solid var(--border)", whiteSpace:"nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isGoalYear  = r.year === retireInYears;
                const isSellStart = sellEnabled && r.year === sellStartYear;
                const rowBg = isGoalYear  ? "rgba(242,135,5,0.06)"
                  : isSellStart ? "rgba(96,165,250,0.06)"
                  : i % 2 === 0 ? "var(--row-odd)" : "transparent";
                const phaseLabel = r.isAccumulating ? "Growth" : r.isSelling ? "Withdrawal" : "Coasting";
                const phaseColor = r.isAccumulating ? C.orange : r.isSelling ? "#60a5fa" : "var(--text4)";
                const phaseBg    = r.isAccumulating ? "rgba(242,135,5,0.08)" : r.isSelling ? "rgba(96,165,250,0.1)" : "var(--bg3)";

                return (
                  <tr key={r.year} style={{ background:rowBg, borderLeft:`2px solid ${isGoalYear ? C.orange : isSellStart ? "#60a5fa" : "transparent"}` }}>
                    <td style={{ padding:"9px 16px", textAlign:"center", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:900, color: isGoalYear ? C.orange : "var(--text)" }}>
                      {r.year === 0 ? "Now" : `${r.year}`}
                      {isGoalYear && <div style={{ fontSize:8, color:C.orange, letterSpacing:"0.06em", lineHeight:1.6, fontWeight:700 }}>GOAL</div>}
                    </td>
                    <td style={{ padding:"9px 16px", textAlign:"right", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color:"var(--text)" }}>
                      {fmtM(r.balance)}
                    </td>
                    {sellEnabled && <>
                      <td style={{ padding:"9px 16px", textAlign:"right", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color: r.soldThisYear > 0 ? "#60a5fa" : "var(--text4)" }}>
                        {r.soldThisYear > 0 ? fmtM(r.soldThisYear) : "—"}
                      </td>
                      <td style={{ padding:"9px 16px", textAlign:"right", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color: r.monthlyIncome > 0 ? "#4ade80" : "var(--text4)" }}>
                        {r.monthlyIncome > 0 ? fmtM(r.monthlyIncome) : "—"}
                      </td>
                      <td style={{ padding:"9px 16px", textAlign:"right", fontFamily:"Montserrat,sans-serif", fontSize:12, color:"var(--text3)" }}>
                        {r.cumulativeSold > 0 ? fmtM(r.cumulativeSold) : "—"}
                      </td>
                    </>}
                    <td style={{ padding:"9px 16px", textAlign:"right" }}>
                      <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:phaseColor, letterSpacing:"0.06em", textTransform:"uppercase", background:phaseBg, padding:"3px 7px", borderRadius:4 }}>
                        {phaseLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
